import React from 'react';
import cx from 'classnames';
import { makeStyles } from '../../theme';
import ReactTextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import { FocusBox } from '../../theme/focus';
import { Visibility, VisibilityOff } from '../icons';
import * as componentStyling from '../styling';
import ValueManager from '../valuemanager';
import Text from '../text';


/**
 * Use `theme settings` as much as possible instead of hard-coded values.
 * More theme settings on: https://material-ui.com/customization/theming/
 */
const commonStyles = makeStyles(
	({ spacing }) => ({
		txtField: {
			marginBottom: spacing(2.5)
		}
	})
)

function ariaLabel() {
	return Object.keys(arguments).reduce((acc, key) => {
		if(arguments[key])acc.push(arguments[key]);
		return acc;
	}, []).join('. ');
}

const TextField = React.memo(
	({className, endadornment, readOnly, ...props}) => {
		const classes = commonStyles();
		return (
			<ReactTextField 
				{...props} 	
				className={cx(classes.txtField, className, componentStyling.Control)} 
				InputProps={{
					readOnly, 
					'aria-label': ariaLabel(props.label, props.helperText),
					endAdornment : endadornment && (<InputAdornment position="end">{endadornment}</InputAdornment>) 
				}}
				/>
		);
	}
);

export default TextField;

export const PasswordField = React.memo(
	({ label, helperText, className, error, defaultValue ='', ...props}) => {
		const classes = commonStyles();
		const [values, setValues] = React.useState({
			password: defaultValue,
			showPassword: false,
		});

		const handleChange = (prop) => (event) => {
			if(props.onChange)props.onChange(event);
			return setValues({ ...values, [prop]: event.target.value })
		};
		const handleClickShowPassword = () => setValues({ ...values, showPassword: !values.showPassword });
		const handleMouseDownPassword = (event) => event.preventDefault();

		return (
			<FormControl className={cx(classes.txtField, className, componentStyling.Control)}>
				<InputLabel>{label}</InputLabel>
				<Input
					{...props}
					aria-label={label}
					type={values.showPassword ? 'text' : 'password'}
					value={values.password}
					onChange={handleChange('password')}
					endAdornment={
					<InputAdornment position="end">
						<FocusBox>
							<IconButton
								aria-label={values.showPassword ? 'Clicking this will hide password text' : 'Clicking this will display password text'}
								onClick={handleClickShowPassword}
								onMouseDown={handleMouseDownPassword}
								>
								{values.showPassword ? <Visibility /> : <VisibilityOff />} 
							</IconButton>
						</FocusBox>
					</InputAdornment>
					}
				/>
				{helperText && (<FormHelperText error={error}>{helperText}</FormHelperText>)}
			</FormControl>
		);
	}
);

/**
 * Use `theme settings` as much as possible instead of hard-coded values.
 * More theme settings on: https://material-ui.com/customization/theming/
 */
const txtAreaStyles = makeStyles({
	txtArea: {
		whiteSpace: "nowrap",
		overflow: "auto",
		overflowWrap: "none",
		'&:disabled': {
			whiteSpace: "pre-wrap"
		}
	}
})

export const TextArea = React.memo(({ className, label, defaultValue, onChange, title, ...props }) => {
	const commonClasses = commonStyles();
	const classes = txtAreaStyles();

	return (
		<React.Fragment>
			{title && <Text variant="h5">{title}</Text>}
			<ValueManager value={defaultValue || label} onChange={onChange}>
				<TextareaAutosize
					{...props}
					className={cx(commonClasses.txtField, classes.txtArea, className)} 
					aria-label={ariaLabel(label, props.helperText)}
					wrap="soft"
					/>
			</ValueManager>
		</React.Fragment>
	);
});